import { ActionOrigin, AnalyticsService } from 'base/analytics';
import { exists, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { extractTreeByName } from 'base/webflow/data_attributes';
import {
  canAddToSlot,
  canDeleteInSlot,
  canReorderInSlot,
  findSlotsIn,
  getCanonicalElementId,
  getCanonicalElementIdPromise,
  getIsElementNonDeletable,
  getParentSlotOfElement,
  getSlotInfoAtNode,
  isElementSelectable,
  maybeFindSlotsIn,
  SlotInfo,
} from 'blocks/component_renderer/slots';
import {
  computeNewLocationMarginOverrides,
  computeOldLocationMarginOverrides,
  marginOverrideToMarginState,
} from 'blocks/component_renderer/slots_margins';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { computed, makeObservable } from 'mobx';
import { getSlotAddPanelOptions } from '../add_panel/slots';
import { findElementReplacableTextContent } from '../ai/element_replacable_text_content';
import {
  anElementInstance,
  ElementInstance,
  ElementInstanceState,
  SlotOverride,
} from '../model';
import { removeAllThreadsFromElement } from '../model_comment_helpers';
import {
  cloneElementPreservingSlotOverrides,
  elementTreeDfs,
  parentElementWithinRoot,
  shallowCloneElementState,
  unrefSection,
} from '../model_helpers';
import { SelectionState } from '../selection/states';
import { SelectionStore } from '../selection/store';
import { SidebarNavigation } from '../sidebar/routes';
import { getResetCopy, getResetElement } from './section';

/** Actions that can be performed on an element-nodeId selection */
export class ElementNodeEditingActions {
  constructor(
    private readonly selection: SelectionStore,
    private readonly self: SelectionState & {
      element: ElementInstance;
    },
    private readonly library: LibraryCache,
    private readonly sidebarNavigation: SidebarNavigation,
    readonly analytics: AnalyticsService,
    readonly flags: FeatureFlagsStore,
    private readonly forParent: (
      self: SelectionState & { element: ElementInstance },
    ) => ElementNodeEditingActions,
  ) {
    makeObservable(this);
  }

  private get section() {
    return unrefSection(this.self.section, this.selection.project)!;
  }

  private get parent() {
    const rootElement = this.section.element.current;
    const parentElement =
      rootElement && parentElementWithinRoot(this.self.element, rootElement);
    if (!parentElement) return;
    // If there is a parent element, we must be in a slot  (no other way for element to have children)
    const tuple = Array.from(parentElement.slotOverrides.entries()).find(
      ([, slot]) => slot.items.includes(this.self.element),
    );
    if (!tuple) return;
    const [slotName, slotOverride] = tuple;
    return { element: parentElement, slotName, slotOverride };
  }

  private async parentSlotPromise() {
    const { parent } = this;
    if (!parent) return;
    const component = await this.library.getPublicComponentJson(
      parent.element.source.slug,
    );
    const slot = findSlotsIn(parent.element.source, component).find(
      (s) => s.name === parent.slotName,
    );
    if (!slot)
      throw new Error(`My slot ${parent.slotName} is not in the component`);
    const type = await this.library.getSlotType(slot.typeId);
    return { info: slot, override: parent.slotOverride, type };
  }

  @computed
  get parentSlot() {
    const { parent } = this;
    if (!parent) return;
    const parentSlots = maybeFindSlotsIn(
      parent.element.source,
      (slug) => this.library.getCachedPublicComponentJson(slug)?.value,
    );
    const info = parentSlots?.find((s) => s.name === parent.slotName);
    if (!info) return;

    // Don't allow editing until the slot type is loaded
    const type = this.library.getCachedSlotType(info.typeId, {
      fetchIfMissing: true,
    })?.value;
    if (!type) return;

    return { info, override: parent.slotOverride, type };
  }

  private async mySlotsPromise() {
    const component = await this.library.getPublicComponentJson(
      this.self.element.source.slug,
    );
    return Promise.all(
      findSlotsIn(this.self.element.source, component).map(async (info) => {
        const override = this.self.element.slotOverrides.get(info.name);
        if (!override) return;
        const type = await this.library.getSlotType(info.typeId);
        return { info, type, override };
      }),
    ).then((l) => l.filter(exists));
  }

  @computed
  get mySlots() {
    const component = this.library.getCachedPublicComponentJson(
      this.self.element.source.slug,
    )?.value;
    if (!component) return [];

    return findSlotsIn(this.self.element.source, component)
      .map((info) => {
        const override = this.self.element.slotOverrides.get(info.name);
        if (!override) return;

        const type = this.library.getCachedSlotType(info.typeId)?.value;
        if (!type) return;

        return { info, type, override };
      })
      .filter(exists);
  }

  @computed
  get delete(): ((origin: ActionOrigin) => void) | string | undefined {
    if (this.self.nodeId) {
      // If you selected a node _inside_ an element, don't offer delete as
      // the user probably wanted to delete the text.  We can only delete
      // the whole element.
      return 'This element cannot be deleted';
    }
    if (!this.parentSlot || !this.parent) {
      // Assume that if we aren't a slot-item selection, we are a text selection.
      // This will be incorrect if we add other selection types.
      return 'This element cannot be deleted';
    }
    const isNonDeletable = getIsElementNonDeletable(
      this.self.element,
      this.library,
    );
    if (isNonDeletable) {
      return 'This element cannot be deleted';
    }
    const slot = this.parentSlot;
    if (!canDeleteInSlot(slot.type)) {
      return 'This element cannot be deleted';
    }
    const newNumItems = slot.override.items.length - 1;
    if (
      // It is cleaner to delete the whole parent than to leave an element
      // which would collapses to nothing anyway.
      // When we didn't have this check, weird stuff happened:
      //   https://www.loom.com/share/2caecac7536241508cfe3672ec0a6cfd
      (newNumItems === 0 ||
        // Must follow the minItems rule
        newNumItems < slot.type.minItems) &&
      isSimpleSingleSlotElement(this.parent.element, this.library)
    ) {
      const editing = this.forParent({
        ...this.self,
        element: this.parent.element,
        nodeId: undefined,
      });
      if (typeof editing.delete === 'function') {
        return editing.delete;
      }
    }
    if (newNumItems < slot.type.minItems) {
      return 'Minimum number of items reached';
    }
    return (origin: ActionOrigin) => {
      const index = slot.override.items.indexOf(this.self.element);
      if (index === -1) return;

      this.analytics.track({
        event: 'blocks/delete-element',
        ...this.analyticsContext,
        origin,
      });

      // Delete any comments on this element.  If we didn't delete
      // them from the section, they'll remain but in a weird position.
      for (const el of elementTreeDfs(this.self.element)) {
        for (const threadId of el.commentThreads.keys()) {
          this.self.section.commentThreads.delete(threadId);
        }
      }

      const parentSel = this.selection.factory.parentOfElementNode(
        this.self,
        this.library,
      );

      const marginOverrides = computeOldLocationMarginOverrides({
        slotItems: slot.override.items,
        slotInfo: slot.info,
        index,
        library: this.library,
      });
      for (const { index: i, marginOverride } of marginOverrides) {
        slot.override.items[i].margin =
          marginOverrideToMarginState(marginOverride);
      }

      slot.override.items.splice(index, 1);

      let newSelectedElement: ElementInstance | undefined;

      const rootElement = this.section.element.current;
      if (!rootElement) return;

      // Find the element that should now be selected.
      if (index < slot.override.items.length) {
        // We didn't delete the last element, so move forward in the slot to
        // find a selectable element.
        for (const item of slot.override.items.slice(index)) {
          for (const el of elementTreeDfs(item)) {
            if (
              isElementSelectable({
                element: el,
                parentSlotType: getParentSlotOfElement({
                  element: el,
                  rootElement,
                  library: this.library,
                })?.type,
                library: this.library,
              })
            ) {
              newSelectedElement = el;
              break;
            }
          }
          if (newSelectedElement) break;
        }
      } else if (index - 1 >= 0 && index - 1 < slot.override.items.length) {
        // We just deleted the last element, so move backward in the slot to
        // find a selectable element.
        for (const item of [...slot.override.items.slice(0, index)].reverse()) {
          for (const el of elementTreeDfs(item, true /* reverse */)) {
            if (
              isElementSelectable({
                element: el,
                parentSlotType: getParentSlotOfElement({
                  element: el,
                  rootElement,
                  library: this.library,
                })?.type,
                library: this.library,
              })
            ) {
              newSelectedElement = el;
              break;
            }
          }
          if (newSelectedElement) break;
        }
      }

      if (newSelectedElement) {
        this.selection.set({
          ...this.self,
          element: newSelectedElement,
          nodeId: undefined,
        });
      } else {
        // Fallback: select the parent element.
        this.selection.set(parentSel);
      }

      this.showElementEditPanel();
    };
  }

  get analyticsContext() {
    return {
      sectionRootSlug: this.section.element.current?.source.slug ?? '',
      slotName: this.parentSlot?.info.name ?? '',
      elementSlug: this.self.element.source.slug,
      elementRootName: this.self.element.source.rootName ?? '',
      elementCanonical:
        getCanonicalElementId(this.self.element, this.library) ?? '',
    };
  }

  private showElementEditPanel() {
    this.sidebarNavigation.goto({ path: 'edit' });
  }

  @computed
  get duplicate(): ((origin: ActionOrigin) => void) | string | undefined {
    if (!this.parentSlot) {
      return 'This element cannot be duplicated';
    }
    const slot = this.parentSlot;

    const canAdd = canAddToSlot(slot.override, slot.type);
    if (!canAdd.result) {
      return canAdd.reason;
    }

    return (origin: ActionOrigin) => {
      const index = slot.override.items.indexOf(this.self.element);

      slot.override.items.splice(
        index + 1,
        0,
        shallowCloneElementState(
          removeAllThreadsFromElement(this.self.element.state),
        ),
      );

      const marginOverrides = computeNewLocationMarginOverrides({
        slotItems: slot.override.items,
        slotInfo: slot.info,
        index: index + 1,
        library: this.library,
      });
      for (const { index: i, marginOverride } of marginOverrides) {
        slot.override.items[i].margin =
          marginOverrideToMarginState(marginOverride);
      }

      this.analytics.track({
        event: 'blocks/duplicate-element',
        ...this.analyticsContext,
        origin,
      });

      const newElement = slot.override.items[index + 1];
      this.selection.set(
        {
          ...this.self,
          element: newElement,
          nodeId: undefined,
        },
        { disableEnsureVisible: true },
      );
      this.showElementEditPanel();
    };
  }

  /* Returns an error message if the element can not be pasted here */
  async pasteElement(
    state: ElementInstanceState,
    origin: ActionOrigin,
  ): Promise<string | undefined> {
    const [canonical, parentSlot, mySlots] = await Promise.all([
      getCanonicalElementIdPromise(state, this.library)
        .then((id) => this.library.getLibraryElement(id))
        .catch(() => null),
      this.parentSlotPromise(),
      this.mySlotsPromise(),
    ]);
    const allSlots = [parentSlot, ...mySlots].filter(exists);
    const slot = allSlots.find(
      (slot) =>
        canonical?.allowedIn.includes(slot.info.typeId) ||
        slot.info.originalItems.find(
          (item) =>
            item.state.source.slug === state.source.slug &&
            item.state.source.rootName === state.source.rootName,
        ),
    );
    if (!slot) return 'This element cannot be pasted here';

    const canAdd = canAddToSlot(slot.override, slot.type);
    if (!canAdd.result) {
      return canAdd.reason;
    }

    this.doPasteElement(state, slot.info, slot.override, origin);
  }

  private doPasteElement(
    state: ElementInstanceState,
    slotInfo: SlotInfo,
    slotOverride: SlotOverride,
    origin: ActionOrigin,
  ) {
    let selectionIndex = slotOverride.items.indexOf(this.self.element);
    if (selectionIndex === -1) {
      selectionIndex = slotOverride.items.length - 1;
    }
    slotOverride.items.splice(selectionIndex + 1, 0, state);

    const marginOverrides = computeNewLocationMarginOverrides({
      slotItems: slotOverride.items,
      slotInfo,
      library: this.library,
      index: selectionIndex + 1,
    });
    for (const { index, marginOverride } of marginOverrides) {
      slotOverride.items[index].margin =
        marginOverrideToMarginState(marginOverride);
    }

    this.analytics.track({
      event: 'blocks/paste-element',
      ...this.analyticsContext,
      origin,
    });

    this.selection.set({
      ...this.self,
      element: slotOverride.items[selectionIndex + 1],
      nodeId: undefined,
    });
    this.showElementEditPanel();
  }

  /** Add the "default" item before or after the current selection */
  @computed
  get addItemAround():
    | ((direction: 'before' | 'after', origin: ActionOrigin) => void)
    | string
    | undefined {
    if (!this.parentSlot) return;
    const slot = this.parentSlot;

    const canAdd = canAddToSlot(slot.override, slot.type);
    if (!canAdd.result) {
      return canAdd.reason;
    }

    return (direction: 'before' | 'after', origin: ActionOrigin) => {
      // This isn't the nicest way to tell whether it's a list-type slot or
      // not... it does the trick for now, but ideally we'd have a proper attr
      // for it on the slot type.
      const isListSlot = slot.type.name.startsWith('List');

      const options = getSlotAddPanelOptions({
        slotType: slot.type,
      });

      if (isListSlot || options.length === 1) {
        // Either we're in a list slot, where we should just duplicate the
        // structure of the selected element; or there is only one add panel
        // option, so we should just add it directly instead of opening the panel.
        const newElementState = isListSlot
          ? cloneElementPreservingSlotOverrides(this.self.element.state)
          : anElementInstance({
              source: { slug: options[0].elementId, rootName: undefined },
            });

        const newIndex =
          Math.max(slot.override.items.indexOf(this.self.element), 0) +
          (direction === 'after' ? 1 : 0);

        slot.override.items.splice(newIndex, 0, newElementState);

        this.analytics.track({
          event: 'blocks/add-element',
          ...this.analyticsContext,
          origin,
        });

        const newElement = slot.override.items[newIndex];
        this.selection.set(
          {
            ...this.self,
            element: newElement,
          },
          { disableEnsureVisible: true },
        );
        this.showElementEditPanel();
      } else {
        // Open element add panel.
        this.selection.set({
          ...this.self,
          addDirection: direction,
        });
        this.sidebarNavigation.goto({ path: 'add' });
      }
    };
  }

  @computed
  get moveInDirection() {
    if (!this.parentSlot) return;
    const slot = this.parentSlot;

    if (!canReorderInSlot(slot.type)) return;

    return (
      direction: 'up' | 'down' | 'left' | 'right',
      origin: ActionOrigin,
    ) => {
      const oldIndex = slot.override.items.indexOf(this.self.element);
      if (oldIndex === -1) return;

      let newIndex: number;
      switch (direction) {
        case 'up':
          if (slot.type.dragHandlePosition !== 'left') return;
          newIndex = oldIndex - 1;
          break;
        case 'down':
          if (slot.type.dragHandlePosition !== 'left') return;
          newIndex = oldIndex + 1;
          break;
        case 'left':
          if (slot.type.dragHandlePosition !== 'top') return;
          newIndex = oldIndex - 1;
          break;
        case 'right':
          if (slot.type.dragHandlePosition !== 'top') return;
          newIndex = oldIndex + 1;
          break;
        default:
          throw new UnreachableError(direction);
      }

      if (newIndex < 0 || newIndex > slot.override.items.length - 1) return;

      this.analytics.track({
        event: 'blocks/move-element',
        ...this.analyticsContext,
        origin,
      });

      const oldLocationMarginOverrides = computeOldLocationMarginOverrides({
        slotItems: slot.override.items,
        slotInfo: slot.info,
        index: oldIndex,
        library: this.library,
      });
      for (const { index, marginOverride } of oldLocationMarginOverrides) {
        slot.override.items[index].margin =
          marginOverrideToMarginState(marginOverride);
      }

      slot.override.items.splice(oldIndex, 1);
      slot.override.items.splice(newIndex, 0, this.self.element.state);

      const newLocationMarginOverrides = computeNewLocationMarginOverrides({
        slotItems: slot.override.items,
        slotInfo: slot.info,
        index: newIndex,
        library: this.library,
      });
      for (const { index, marginOverride } of newLocationMarginOverrides) {
        slot.override.items[index].margin =
          marginOverrideToMarginState(marginOverride);
      }

      const newElement = slot.override.items[newIndex];
      this.selection.set(
        { ...this.self, element: newElement },
        { disableEnsureVisible: true },
      );
    };
  }

  @computed get resetCopy() {
    return getResetCopy(this.self.element);
  }

  @computed get resetElements() {
    return getResetElement(this.self.element, this.library);
  }
}

/**
 * An element with just a single slot & "nothing else".
 *
 * It is hard to define "nothing else", so we do so strictly - every the slot
 * must only be wrapped.  I.e. the dom structure would look like:
 *
 *   - div.root
 *     - div.wrapper-1
 *       ...
 *         - div.wrapper-n
 *           - slot
 **/
function isSimpleSingleSlotElement(
  element: ElementInstance,
  library: LibraryCache,
): boolean {
  const component = library.getCachedPublicComponentJson(element.source.slug, {
    fetchIfMissing: true,
  })?.value;
  // You can't edit something that isn't loaded:
  if (!component) return false;
  let at = extractTreeByName(component, element.source.rootName);
  while (at) {
    if (at.text) return false;
    if (getSlotInfoAtNode(at, element.source, component)) {
      // we found the 1 slot in this element!
      return true;
    }
    if (at.children.length !== 1) return false;
    at = at.children[0];
  }
  return false; // unreachable
}

/** Used to group elements, for analytics */
function getOriginalText(
  {
    element,
    nodeId,
  }: {
    element: ElementInstance | ElementInstanceState;
    nodeId?: string;
  },
  library: LibraryCache,
): string | undefined {
  const text = findElementReplacableTextContent(element, library).filter(
    // ignore text inside sub-elements
    (t) => t.element === element,
  );
  if (nodeId === undefined) {
    return text.length === 1 ? text[0].original : undefined;
  } else {
    return text.find((t) => t.nodeId === nodeId)?.original;
  }
}
